import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Box, CloseButton, Flex, Spinner, Text} from "@chakra-ui/react";
import type Player from "video.js/dist/types/player";
import VideoJS from "../MediaPlayer.tsx";
import TranslationQCPanel from "../TranslationQCDrawer/TranslationQCPanel.tsx";
import {getProjectStreamLinks} from "../../services/projectService.ts";
import type {IProjectStreamLinks} from "../../models/interfaces/projectStreamLinks.interface.ts";
import type {ISubtitleTrack} from "../../models/interfaces/subtitleTrack.interface.ts";
import type {IStreamLink} from "../../models/interfaces/streamLinks.interface.ts";
import iso6391 from "iso-639-1";
import {usePreviewQC} from "../../context/usePreviewQC.ts";

interface PreviewWithQCSessionProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string | null;
}

const useIsDesktop = () => {
  const [mediaQuery] = useState(() => window.matchMedia?.("(min-width: 48em)"));
  const [isDesktop, setIsDesktop] = useState(() => mediaQuery?.matches === true);
  useEffect(() => {
    if (!mediaQuery) return;
    const handleChange = (event: MediaQueryListEvent) => setIsDesktop(event.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [mediaQuery]);
  return isDesktop;
};

const PreviewWithQCSession: React.FC<PreviewWithQCSessionProps> = ({isOpen, onClose, projectId}) => {
  const isDesktop = useIsDesktop();
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [subtitlesTracks, setSubtitlesTracks] = useState<ISubtitleTrack[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const lastProjectIdRef = useRef<string | null>(null);
  const sessionRef = useRef<HTMLDivElement | null>(null);
  const {resetSession, setPreviewSessionActive, setCurrentTime, playerRef, selectedLanguageCode} = usePreviewQC();

  const resetLocalSession = useCallback(() => {
    lastProjectIdRef.current = null;
    setVideoUrl(null);
    setSubtitlesTracks([]);
    setIsLoading(false);
  }, []);

  const closeSession = useCallback(() => {
    resetLocalSession();
    resetSession();
    onClose();
  }, [onClose, resetLocalSession, resetSession]);

  useEffect(() => () => {
    lastProjectIdRef.current = null;
    resetSession();
  }, [resetSession]);

  useEffect(() => {
    if (!isOpen) return;
    setPreviewSessionActive(true);
    const focusTimer = window.setTimeout(() => sessionRef.current?.focus(), 0);
    return () => window.clearTimeout(focusTimer);
  }, [isOpen, setPreviewSessionActive]);

  useEffect(() => {
    if (!isOpen || !projectId || projectId === lastProjectIdRef.current) return;
    let isCancelled = false;
    const fetchStreamLinks = async () => {
      setIsLoading(true);
      try {
        const projectStreamLinks: IProjectStreamLinks = await getProjectStreamLinks(projectId);
        if (isCancelled) return;
        setVideoUrl(`${projectStreamLinks.video.url}?${projectStreamLinks.video.sasToken}`);
        setSubtitlesTracks(Object.keys(projectStreamLinks.subtitles).map((languageCode, index) => {
          const languageStreamLink: IStreamLink = projectStreamLinks.subtitles[languageCode];
          return {src: `${languageStreamLink.url}?${languageStreamLink.sasToken}`, label: iso6391.getName(languageCode), srclang: languageCode, default: index === 0};
        }));
        lastProjectIdRef.current = projectId;
      } catch (error) {
        if (isCancelled) return;
        console.error("Failed to load video stream links:", error);
        setVideoUrl(null);
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    };
    void fetchStreamLinks();
    return () => {
      isCancelled = true;
    };
  }, [isOpen, projectId]);

  const videoJsOptions = useMemo(() => ({autoplay: true, controls: false, responsive: true, fluid: false, fill: true, sources: videoUrl ? [{src: videoUrl, type: "video/mp4"}] : []}), [videoUrl]);
  const handlePlayerReady = useCallback((player: Player) => { playerRef.current = player; }, [playerRef]);
  const handleTimeUpdate = useCallback((time: number) => { setCurrentTime(time); }, [setCurrentTime]);

  if (!isOpen || !projectId) return null;

  return (
    <Box ref={sessionRef} data-testid="preview-qc-session" role="dialog" aria-label="Preview QC session" tabIndex={-1} position="fixed" inset={0} zIndex={1500} bg="blackAlpha.800" display="flex" flexDirection={{base: "column", md: "row"}} overflow="hidden">
      <Flex flex={{base: "0 0 45%", md: "1"}} minW={0} minH={0} bg={{base: "black", md: "transparent"}} position="relative" direction="column">
        <CloseButton position="absolute" top={3} right={3} zIndex={2} color="white" onClick={closeSession} />
        <Box flex="1" minH={0} display="flex" flexDirection="column">
          {isLoading ? <Spinner size="xl" color="white" m="auto" /> : videoUrl ? <VideoJS key={isDesktop ? 'intrinsic' : 'fill'} fitMode={isDesktop ? 'intrinsic' : 'fill'} options={videoJsOptions} subtitles={subtitlesTracks} subtitleLanguageCode={selectedLanguageCode} onReady={handlePlayerReady} onTimeUpdate={handleTimeUpdate} /> : <Text color="white" m="auto">No video available.</Text>}
        </Box>
      </Flex>
      <Box data-testid="preview-qc-panel" w={{base: "100%", md: "32rem"}} maxW={{base: "100%", md: "32rem"}} flexShrink={0} h={{base: "55%", md: "full"}}><TranslationQCPanel projectId={projectId} onClose={closeSession} showPreviewControls /></Box>
    </Box>
  );
};

export default PreviewWithQCSession;
